import { AUTH_BASE_URL, VERSION, apiClientHeaders } from "./config";
import { markWebVersionCurrent, reloadForNewWebVersion } from "./versionRefresh";

let refreshPromise = null; // Global variable to track refresh token requests

const isAppUpdateError = (data) => {
    if (data.response_code !== 406) return false;
    const buttonKey = data.response?.buttons?.[0]?.key;
    const title = data.response?.title || "";
    return buttonKey === "app_update" || title === "Update App";
};

const throwIfApiError = async (data) => {
    if (data.response_code === 200) {
        markWebVersionCurrent();
        return data.response;
    }

    if (isAppUpdateError(data)) {
        const reloading = await reloadForNewWebVersion();
        if (reloading) {
            await new Promise(() => {});
        }
        const customError = new Error(data.message || "Update App");
        customError.code = 406;
        customError.type = "app_update";
        throw customError;
    }

    if (data.response_code === 412) {
        const customError = new Error("AccessDenied");
        customError.code = 412;
        throw customError;
    }

    if (data.response_code === 406) {
        const buttonKey = data.response?.buttons?.[0]?.key;
        const title = data.response?.title || "";
        const customError = new Error(data.message || title || "Blocked");
        customError.code = 406;
        if (buttonKey === "account_not_found" || title.includes("Not Found")) {
            customError.type = "account_not_found";
        } else {
            customError.type = "account_blocked";
        }
        throw customError;
    }

    if (data.response_code === 404) {
        const customError = new Error("NotFound");
        customError.code = 404;
        throw customError;
    }

    if (data.response_code === 400) {
        const customError = new Error(data.message);
        customError.code = 400;
        throw customError;
    }

    throw new Error(data.message || "API Error");
};

export const refreshAccessToken = async () => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            try {
                const refreshToken = localStorage.getItem("refreshToken");
                if (!refreshToken) throw new Error("No refresh token available");

                const response = await fetch(`${AUTH_BASE_URL}/${VERSION}/user/token/refresh`, {
                    method: "GET",
                    headers: apiClientHeaders({ Authorization: `Bearer ${refreshToken}` }),
                });

                const data = await response.json();

                if (data.response_code === 200) {
                    localStorage.setItem("accessToken", data.response.accessToken);
                    localStorage.setItem("refreshToken", data.response.refreshToken);
                    return data.response.accessToken; // Return new access token
                } else {
                    throw new Error("Failed to refresh token");
                }
            } catch (error) {
                console.error("Token Refresh Error:", error);
                handleLogout();
                return null;
            } finally {
                refreshPromise = null; // Reset after completion
            }
        })();
    }

    return refreshPromise; // Return the same promise for all requests
};

export const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("username");
    const currentPath = window.location.pathname + window.location.search;
    console.log(currentPath)
    if (currentPath !== "/" && currentPath !== "/otp" && currentPath !== "/dashboard") {
        sessionStorage.setItem("redirectPath", currentPath);
    }
    window.location.href = "/"; // Redirect to login page
};

export const apiFetch = async (endpoint, method, body, headers) => {
    try {
        let accessToken = localStorage.getItem("accessToken");

        let options = {
            method,
            headers: apiClientHeaders({
                ...(headers || {}),
                Authorization: `Bearer ${accessToken}`,
            }),
        };

        if (body) {
            options.body = body;
        }

        let response = await fetch(endpoint, options);
        let data = await response.json();
        console.log(data)

        if (data.response_code === 200) {
            markWebVersionCurrent();
            return data.response; // Return actual data
        }

        if (data.response_code === 401 || data.response_code === 403) {
            console.warn("Access token expired, attempting refresh...");

            const newAccessToken = await refreshAccessToken();

            if (newAccessToken) {
                console.log("Token successfully refreshed");

                // Retry the original request with the new token
                options.headers.Authorization = `Bearer ${newAccessToken}`;
                response = await fetch(endpoint, options);
                data = await response.json();

                console.log(data)

                if (data.response_code === 200) {
                    markWebVersionCurrent();
                    return data.response;
                }

                if (data.response_code !== 401 && data.response_code !== 403) {
                    return throwIfApiError(data);
                }
            }

            handleLogout(); // If refresh failed, log out
            return null;
        }

        return throwIfApiError(data);
    } catch (error) {
        console.error("API Fetch Error:", error);
        throw error;
    }
};
